'use client';

import React from 'react';

import { AnimatedContentSwitcher } from '@components/AnimatedContentSwitcher/AnimatedContentSwitcher';
import { FeaturesList } from '@components/FeaturesList/FeaturesList';
import { Section } from '@components/Section/Section';
import { Tabbar } from '@components/Tabs/Tabbar/Tabbar';
import { externalRoutes } from '@routes';

import styles from './FeatureTabs.module.scss';
import AnnotationAlertIcon from '@components/Images/Icons/annotationAlert.svg';
import BarChartSquareIcon from '@components/Images/Icons/barChartSquare.svg';
import FileIcon from '@components/Images/Icons/file.svg';
import FileLinesIcon from '@components/Images/Icons/fileLines.svg';
import FileLockIcon from '@components/Images/Icons/fileLock.svg';
import FileShieldIcon from '@components/Images/Icons/fileShield.svg';
import FlipLeftIcon from '@components/Images/Icons/flipLeft.svg';
import FolderOpenIcon from '@components/Images/Icons/folderOpen.svg';
import ImageQuestionIcon from '@components/Images/Icons/imageQuestion.svg';
import KeyIcon from '@components/Images/Icons/key.svg';
import LayersIcon from '@components/Images/Icons/layersFeat.svg';
import ListIcon from '@components/Images/Icons/list.svg';
import LockIcon from '@components/Images/Icons/lock.svg';
import MediaStripIcon from '@components/Images/Icons/mediaStrip.svg';
import PlayIcon from '@components/Images/Icons/play.svg';
import PointerIcon from '@components/Images/Icons/pointer.svg';
import ShieldCheckIcon from '@components/Images/Icons/shieldCheck.svg';
import UnionIcon from '@components/Images/Icons/union.svg';
import UsersProfilesIcon from '@components/Images/Icons/usersProfiles.svg';
import WaveFormIcon from '@components/Images/Icons/waveform.svg';

const getItems = (withClientPanelButton: boolean) => [
  {
    name: 'Content delivery',
    content: (
      <FeaturesList
        data={[
          {
            icon: <FileIcon />,
            title: 'Object Storage',
            description:
              'Choose CDN77s Object Storage with S3-compatible API as your Origin and experience faster, more convenient data transfer.',
            href: `${externalRoutes.client.cdnStorage}`,
          },
          {
            icon: <LayersIcon />,
            title: 'Multilayer cache',
            description:
              'Our multilayer caching will improve your caching rate, leading to better content delivery and reduced load on your origin.',
            href: `${externalRoutes.client.multilayerCache}`,
          },
          {
            icon: <UnionIcon />,
            title: 'Request collapsing',
            description:
              'Reduce the load on your origin by consolidating multiple requests for the same content into a single one.',
            href: `${externalRoutes.client.requestCollapsing}`,
          },
          {
            icon: <WaveFormIcon />,
            title: 'Live stream ingesting and packaging',
            description:
              'Stream live content to audiences worldwide, with optimal playback on any device, any connection with our real-time transcoding.',
            href: `${externalRoutes.client.liveStreaming}`,
          },
          {
            icon: <MediaStripIcon />,
            title: 'DVR and DRM support',
            description:
              'CDN77 provides a secure and versatile solution with video archive viewing, and content protection.',
            href: `${externalRoutes.client.liveToVod}`,
          },
          {
            icon: <PlayIcon />,
            title: 'VOD packaging and transcoding',
            description:
              'Simplify VOD delivery by automatically converting and packaging video files into multiple formats.',
            href: `${externalRoutes.client.encodingTranscoding}`,
          },
          {
            icon: <FileLockIcon />,
            title: 'Instant SSL/SNI support',
            description:
              'Use our free InstantSSL or upload your own certificate  to ensure your content is always delivered securely.',
            href: `${externalRoutes.client.sslTls}`,
          },
          {
            icon: <FlipLeftIcon />,
            title: 'Download rate limit',
            description: 'Set and adjust bandwidth and request limits to fit your specific needs.',
            href: `${externalRoutes.client.rateLimiting}`,
          },
        ]}
      />
    ),
  },
  {
    name: 'Security & analytics',
    content: (
      <FeaturesList
        data={[
          {
            icon: <ShieldCheckIcon />,
            title: 'Always-on DDoS Protection',
            description:
              'Protect your origin from malicious traffic by mitigating DDoS attacks in real-time, without impacting your users.',
          },
          {
            icon: <LockIcon />,
            title: 'TLS',
            description:
              'Ensure the privacy and integrity of the communication between your origin and your users.',
            href: `${externalRoutes.client.sslTls}`,
          },
          {
            icon: <FileShieldIcon />,
            title: 'Origin protection',
            description:
              'Secure your origin server from direct access and prevent unauthorized requests.',
            href: `${externalRoutes.client.originProtection}`,
          },
          {
            icon: <BarChartSquareIcon />,
            title: 'Real-time analytics',
            description:
              'Track content delivery performance, user insights, and other key metrics in real time.',
          },
          {
            icon: <FileLinesIcon />,
            title: 'CDN Raw Logs',
            description:
              'Access detailed traffic logs to troubleshoot and analyze your content delivery.',
          },
          {
            icon: <UsersProfilesIcon />,
            title: 'Role-based access',
            description: 'Grant specific permissions and control access to your account and files.',
          },
        ]}
      />
    ),
  },
  {
    name: 'Custom rules & configs',
    content: (
      <FeaturesList
        data={[
          {
            icon: <ListIcon />,
            title: 'Customizable Cache Rules',
            description: 'Configure and optimize caching behavior based on your specific needs.',
          },
          {
            icon: <AnnotationAlertIcon />,
            title: 'Response manipulation',
            description:
              'Customization involves modifying the content sent to clients, allowing for the manipulation of the response (body, headers, etc.) before it is delivered to the end user.',
            href: `${externalRoutes.client.responseManipulation}`,
          },
          {
            icon: <FlipLeftIcon />,
            title: 'Customizable URL redirects',
            description: 'Redirect traffic from one URL to another and manage redirect chains.',
            href: `${externalRoutes.client.urlRedirects}`,
          },
          {
            icon: <PointerIcon />,
            title: 'Custom parameter-based path handling',
            description: 'Modify or route traffic based on specific parameters in the URL path.',
            href: `${externalRoutes.client.pathHandling}`,
          },
          {
            icon: <KeyIcon />,
            title: 'Customizable Cache Keys',
            description:
              'Define custom cache keys based on specific URL parameters or request headers.',
            href: `${externalRoutes.client.cacheKeys}`,
          },
          {
            icon: <FolderOpenIcon />,
            title: 'Custom logging and log cluster integrations',
            description:
              'Obtain advanced analytics and insights and send them to external log management systems.',
            href: `${externalRoutes.client.clusterIntegrations}`,
          },
          {
            icon: <BarChartSquareIcon />,
            title: 'Custom HTML error response pages',
            description:
              'Create and serve custom error pages for HTTP error codes to provide a better user experience and brand consistency.',
            href: `${externalRoutes.client.errorResponse}`,
          },
          {
            icon: <ImageQuestionIcon />,
            title: 'File type rules',
            description:
              'When a client requests content, the CDN`s edge server evaluates the file type of the requested content and applies the defined rule set.',
            href: `${externalRoutes.client.fileRules}`,
          },
        ]}
        withClientPanelButton={withClientPanelButton}
      />
    ),
  },
];

interface Props {
  withClientPanelButton?: boolean;
}

export const FeatureTabs: React.FC<Props> = ({ withClientPanelButton = false }) => {
  const items = getItems(withClientPanelButton);
  const [activeTab, setActiveTab] = React.useState(items[0].name);

  const content = React.useMemo(
    () => items.find(({ name }) => name === activeTab)?.content ?? null,
    [activeTab],
  );

  return (
    <div>
      <Section className={styles.tabsWrapper} padding={0} tag="div">
        <Tabbar
          activeTab={activeTab}
          items={items.map(({ name }) => name)}
          theme="transparent-border"
          width="auto"
          onClick={setActiveTab}
        />
      </Section>
      <Section padding={{ top: 60, bottom: 120 }} theme="gray">
        <AnimatedContentSwitcher>
          <div key={activeTab}>{content}</div>
        </AnimatedContentSwitcher>
      </Section>
    </div>
  );
};
